import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { Field, formValueSelector, reduxForm } from "redux-form";
import { Button, Form, Grid, Header, Message, Segment } from "semantic-ui-react";
import { privacyPolicyLink, termsOfServiceLink } from "../../config/config";
import { doesNotExist, exists } from "../../utils/utils";
import BackRow from "../common/BackRow";
import CustomLoader from "../common/CustomLoader";
import CheckboxInput from "../forms/CheckboxInput";
import DropdownInput from "../forms/DropdownInput";
import TextInput from "../forms/TextInput";

/**
 * Account & user creation form.
 */
class CreateAccount extends React.Component {
  static propTypes = {
    /** Function to call when submitting form. */
    onSubmit: PropTypes.func,
    /** Creating a demo user?  */
    isDemo: PropTypes.bool
  };

  constructor(props) {
    super(props);
    this.state = {
      isDemo: props.isDemo
    };
  }

  /**
   * Update cities list based on selected region
   *
   * @param {String} value      - Selected city value
   * @private
   */
  _onRegionChange = value => {
    const { cities } = this.props.regions.find(region => region.id === value);
    this.props.change("organization.city", cities[0].name);
  };

  _isDemoCheckboxChange = value => {
    this.setState({ isDemo: value });
    if (value === true) {
      this.props.change("organization.city", this.props.selectedRegion.cities[0].name);
    }
  };

  render() {
    if (
      this.props.submittingForm ||
      this.props.gettingRegions ||
      doesNotExist(this.props.regions)
    ) {
      return <CustomLoader />;
    }
    const { regions, cities, isDemo } = this.props;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          height: "100vh"
        }}
      >
        <Grid textAlign="center" style={{ height: "100%" }} verticalAlign="middle">
          <Grid.Column style={{ maxWidth: 450 }}>
            <Header as="h1" textAlign="center">
              Create your Penciler account
            </Header>
            <Form onSubmit={this.props.handleSubmit} size="large" style={{ textAlign: "left" }}>
              <Segment>
                <Field
                  component={TextInput}
                  label="Account name (company)"
                  name="organization.name"
                  icon="building outline"
                  iconPosition="left"
                  placeholder="Company"
                  required
                />
                <Field
                  component={TextInput}
                  label="First name"
                  name="firstName"
                  icon="user"
                  iconPosition="left"
                  placeholder="First name"
                  required
                />
                <Field
                  component={TextInput}
                  label="Last name"
                  icon="user"
                  iconPosition="left"
                  name="lastName"
                  placeholder="Last name"
                  required
                />
                <Field
                  component={TextInput}
                  label="Email"
                  name="email"
                  icon="at"
                  iconPosition="left"
                  placeholder="example@penciler.org"
                  required
                />
                <Field
                  label="Region"
                  component={DropdownInput}
                  name="organization.region"
                  placeholder="Select region"
                  fluid
                  selection
                  options={
                    exists(regions)
                      ? regions.map(region => ({
                          key: region.id,
                          text: region.name,
                          value: region.id
                        }))
                      : []
                  }
                  disabled={doesNotExist(regions)}
                  onChange={this._onRegionChange}
                  required
                />
                <Field
                  label="City"
                  component={DropdownInput}
                  name="organization.city"
                  placeholder="Select city"
                  fluid
                  selection
                  options={
                    exists(cities)
                      ? cities.map(city => ({
                          key: city.id,
                          text: city.name,
                          value: city.name
                        }))
                      : []
                  }
                  disabled={doesNotExist(cities) || isDemo}
                  required
                />
                <Field
                  name="demo"
                  label="Create demo account"
                  component={CheckboxInput}
                  onChange={this._isDemoCheckboxChange}
                  icon="map marker alternate"
                  toggle
                />
                <Message info>
                  <p>An account confirmation email will be sent.</p>
                </Message>
                <Message info>
                  {this.state.isDemo ? null : (
                    <p>The creator of this account is automatically the account Administrator.</p>
                  )}
                  {this.state.isDemo ? null : (
                    <p>
                      Additional Administrators, as well as Editors and Viewers, can be managed once
                      your account is created.
                    </p>
                  )}
                  <p>
                    By creating this account you agree to our{" "}
                    <a href={termsOfServiceLink} download>
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a href={privacyPolicyLink} download>
                      Privacy Policy
                    </a>
                    .
                  </p>
                </Message>
                <Button.Group widths="2">
                  <BackRow rawButton content="Cancel" displayIcon={false} />
                  <Button.Or />
                  <Button primary type="submit">
                    Create Account
                  </Button>
                </Button.Group>
              </Segment>
            </Form>
            {this.props.createAccountFailed ? (
              <Message negative>{this.props.createAccountFailedMessage}</Message>
            ) : (
              <div />
            )}
          </Grid.Column>
        </Grid>
      </div>
    );
  }
}

const form = "createAccount";
const CreateAccountForm = reduxForm({
  form: form,
  enableReinitialize: true
})(CreateAccount);

const selector = formValueSelector(form);
const mapStateToProps = (state, props) => {
  const selectedRegion = exists(state.regions.regions)
    ? state.regions.regions.find(region => region.id === selector(state, "organization.region"))
    : null;
  const cities = exists(selectedRegion) ? selectedRegion.cities : null;
  return {
    createAccountFailed: state.newAccount.createAccountFailed,
    createAccountFailedMessage: state.newAccount.createAccountFailedMessage,
    submittingForm: state.newAccount.submittingForm,
    initialValues: {
      organization: {
        region: exists(state.regions.regions) ? state.regions.regions[0].id : null,
        city: exists(state.regions.regions) ? state.regions.regions[0].cities[0].name : null
      },
      demo: props.isDemo
    },
    regions: state.regions.regions,
    gettingRegions: state.regions.gettingRegions,
    isDemo: selector(state, "demo"),
    cities,
    selectedRegion
  };
};

export default connect(mapStateToProps)(CreateAccountForm);
